/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import 'react-calendar/dist/Calendar.css';
import Calendar from 'react-calendar';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import Select from 'react-select';

import { useDispatch, useSelector } from 'react-redux';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setSelectedDate,
  setSelectedMonth,
  setFirstMonthInPicker,
  fetchCalendarPickerItems,
  setSelectedWeekViewDayToggle,
  setIsPrimaryAnimatedSideBarOpen,
  setPrimarySideBarDataTag,
} from '../../ducks/calendarActions';
import usePreSaveNavigationGuard from '../../hooks/usePreSaveNavigationGuard';

const StyledCalendarWrapper = styled('div')`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;
  position: relative;
   ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(136, 136, 136, 0.5);
    border-radius: 10px;
    transition: background-color 0.3s ease; 
  }
  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0); 
  }
  :hover::-webkit-scrollbar-thumb {
    background-color: rgba(136, 136, 136, 1); 
  }
  scrollbar-width: thin; 
  scrollbar-color: rgba(136, 136, 136, 0.5) transparent;
`;

const DateSelectorDropdown = styled('div')`
  background: white;
  border-radius: 8px;
  margin-bottom: ${(props) => (props.isVisible ? '16px' : '0px')};
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  max-height: ${(props) => (props.isVisible ? '200px' : '0px')};
  opacity: ${(props) => (props.isVisible ? '1' : '0')};
  overflow: visible;
  transform: ${(props) => (props.isVisible ? 'translateY(0)' : 'translateY(-10px)')};
  z-index: 10;
  position: relative;
  display: flex;
  justify-content: space-evenly;
  padding-top: 1px;
`;

const SelectContainer = styled('div')`
  flex: 1;
  min-width: 80px;
  max-width: 120px;
  position: relative;
  z-index: 20;
`;

const GoButton = styled('button')`
  background: #444444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-family: 'Nunito Sans';
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-width: 40px;
  
  &:hover {
    background: #333333;
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const ClickableNavigationLabel = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-weight: 700;
  font-size: 16px;
  color: #444444;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const StyledCalendar = styled(Calendar)`
  border: none;
  width: 100%;
  font-family: 'Nunito Sans';

  .react-calendar__month-view__days__day--neighboringMonth {
    pointer-events: none;
  }
  .react-calendar__navigation__label {

  }

  .react-calendar__navigation button:enabled:focus, .react-calendar__navigation button:enabled:hover {
    background-color: white;
  }

/** this applies to all tiles that aren't disabled */
  .react-calendar__tile:enabled {
    color: #4F4F4F;
    font-size: 13px;
    border-radius: 4px;
    height: 50px;
    font-family: 'Nunito Sans';
  }
 
  /** I believe this is the tile the cursor is over */
  .react-calendar__tile--active {
    background: none;
  }

  react-calendar__tile--active:enabled:hover, .react-calendar__tile--active:enabled:focus {
    background: white;
  }
  .react-calendar__tile--now { 
    background: none !important;
    font-weight: bold;
  }
  
  /** this is the weekday tiles */
  .react-calendar__month-view__weekdays__weekday {
    font-family: 'Nunito Sans';
    abbr {
      cursor: default;
      text-decoration: none;
      color: #444444;
    }
  }

  /** calendar month header (e.g. July 2024) */
  .react-calendar__navigation button:enabled {
    font-family: Nunito Sans;
  }

  .react-calendar__tile {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    abbr {
        display: none;
      }
  }
  
  .react-calendar__tile:enabled:hover, .react-calendar__tile:enabled:focus {
    background-color: none;
    background: none;
  }

`;

const DotIndicator = styled('div')`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(props) => {
    if (props.isSelected && props.weekPosition !== undefined) {
      /** Week view: white dot for Sunday, dark dot for other days */
      if (props.isCurrentDate) return 'white';
      return props.weekPosition === 0 ? '#444444' : '#444444';
    }
    return props.isCurrentDate ? 'white' : '#444444';
  }};
  opacity: ${(props) => (props.hasWorkout ? '1' : '0')};
  margin-top: 3px;
`;

const DateCircle = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      return '100%'; /** Full width in week view to eliminate gaps */
    }
    return '42px';
  }};
  height: 42px;
  margin: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      /** Add negative margins to create overlap between tiles */
      return '0 -2px';
    }
    return '0';
  }};
  border-radius: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      /** Week view: only round outer edges for seamless appearance */
      if (props.isCurrentDate) return '4px';
      if (props.weekPosition === 0) return '4px 0 0 4px'; /** First day (Sunday) - rounded left */
      if (props.weekPosition === 6) return '0 4px 4px 0'; /** Last day (Saturday) - rounded right */
      return '0'; /** Middle days - no rounding for seamless connection */
    }
    return props.selected || props.isCurrentDate ? '4px' : '50%';
  }};
  color: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      /** Week view: white text for Sunday, dark text for other days */
      if (props.selected && !props.isCurrentDate) return '#444444';
      if (props.isCurrentDate) return 'white';
      return props.isInMonth ? '#444444' : '#9E9E9E';
    }
    if (props.selected && !props.isCurrentDate) return '#444444';
    if (props.isCurrentDate) return 'white';
    return props.isInMonth ? '#444444' : '#9E9E9E';
  }};
  background: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      /** Week view: different colors for first day vs other days */
      if (props.isCurrentDate) return '#444444';
      return props.weekPosition === 0 ? '#f6f6f6' : '#f6f6f6';
    }
    if (props.isCurrentDate) return '#444444';
    return props.selected ? '#f6f6f6' : 'none';
  }};
  font-size: 13px;
  position: absolute;
  z-index: ${(props) => (props.selected ? '9' : '1')};
  
  :hover {
    background-color: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      if (props.isCurrentDate) return '#444444';
      return props.selected ? '#f6f6f6' : '#f6f6f6';
    }
    if (props.isCurrentDate) return '#444444';
    return props.selected ? '#f6f6f6' : '#f6f6f6';
  }};
    border-radius: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      if (props.isCurrentDate) return '4px';
      if (props.weekPosition === 0) return '4px 0 0 4px';
      if (props.weekPosition === 6) return '0 4px 4px 0';
      return '0';
    }
    return '4px';
  }};
    width: ${(props) => {
    if (props.selected && props.weekPosition !== undefined) {
      return '100%';
    }
    return '42px';
  }};
    height: 42px;
  }
`;

const CalendarCalendarPicker = () => {
  const dispatch = useDispatch();
  const guardNavigation = usePreSaveNavigationGuard();
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const currentlyDisplayedWeekdays = useSelector(
    (state) => state.calendar.data.currentlyDisplayedWeekdays,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const [currentMonth, setCurrentMonth] = useState(moment(selectedDate).startOf('month').toDate());

  const [nextMonth, setNextMonth] = useState(moment(selectedDate).add(1, 'month').startOf('month').toDate());

  /** Date selector state */
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [selectedYear, setSelectedYear] = useState(moment(selectedDate).year());
  const [selectedMonthNumber, setSelectedMonthNumber] = useState(moment(selectedDate).month() + 1);

  const calendarPickerItems = useSelector((state) => state.calendar.data.calendarPickerItems);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const viewType = useSelector((state) => state.calendar.data.viewType);

  /** Generate year options (2012 to current year + 3) */
  const currentYear = moment().year();
  const yearOptions = [];
  for (let year = 2012; year <= currentYear + 3; year += 1) {
    yearOptions.push({ value: year, label: year.toString() });
  }

  /** Generate month options */
  const monthOptions = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' },
  ];

  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  /** we need a useEffect here that only runs when the calendar picker is rendered
   * aka when the side bar calendar picker is in view. This will watch for changes in the
   * calendar items and make sure the dots are up to date with changes
   */
  useEffect(() => {
    if (!currentUser?.accountCode) return;
  /** startDate = first day of the current month */
    const startDate = moment(currentMonth)
      .startOf('month')
      .format('YYYY-MM-DD');

    /** endDate = 69 days after startDate */
    const endDate = moment(startDate)
      .add(69, 'days')
      .format('YYYY-MM-DD');

    dispatch(fetchCalendarPickerItems(currentUser, startDate, endDate));
  }, [calendarItems, currentUser?.accountCode]);

  const handleDateChange = (date) => guardNavigation(() => {
    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setSelectedWeekViewDayToggle('Everyday'));
    }
    // When a date is directly clicked, update the selectedDate
    dispatch(setSelectedDate(date));
    // Also update the selectedMonth to match the month of the selected date
    // This ensures proper data loading when selecting dates
    const selectedMonthNum = moment(date).month() + 1;
    dispatch(setSelectedMonth(selectedMonthNum));

    // Close the sidebar on mobile after selecting a date
    if (window.innerWidth < 768) {
      dispatch(setIsPrimaryAnimatedSideBarOpen(false));
      dispatch(setPrimarySideBarDataTag(null));
    }
  });

  // Function to load data for both visible months
  const loadDataForMonths = (firstMonth, secondMonth) => {
    const firstMonthStart = moment(firstMonth).startOf('month').format('YYYY-MM-DD');
    const secondMonthEnd = moment(secondMonth).endOf('month').format('YYYY-MM-DD');

    // Only proceed if we have the required data
    if (!currentUser?.accountCode) return;

    // Fetch data based on calendar or athlete selection
    if (Object.keys(selectedReduxCalendar).length && selectedReduxCalendar?.id) {
      dispatch(fetchCalendarPickerItems(
        currentUser,
        firstMonthStart,
        secondMonthEnd,
      ));
    } else if (Object.keys(selectedReduxAthlete).length && selectedReduxAthlete?.id) {
      dispatch(fetchCalendarPickerItems(
        currentUser,
        firstMonthStart,
        secondMonthEnd,
      ));
    }
  };

  // Update navigation to leave selectedDate completely unchanged
  const handleNavigation = (direction) => {
    let newCurrentMonth;
    let newNextMonth;

    if (direction === 'next') {
      newCurrentMonth = moment(currentMonth).add(1, 'month').startOf('month').toDate();
      newNextMonth = moment(nextMonth).add(1, 'month').startOf('month').toDate();
    } else if (direction === 'prev') {
      newCurrentMonth = moment(currentMonth).subtract(1, 'month').startOf('month').toDate();
      newNextMonth = moment(nextMonth).subtract(1, 'month').startOf('month').toDate();
    }

    setCurrentMonth(newCurrentMonth);
    setNextMonth(newNextMonth);

    // Update Redux's selectedMonth to match the new current month
    const newCurrentMonthNumber = moment(newCurrentMonth).month() + 1;
    dispatch(setSelectedMonth(newCurrentMonthNumber));
    dispatch(setFirstMonthInPicker(newCurrentMonth));

    // Load data for both visible months
    loadDataForMonths(newCurrentMonth, newNextMonth);
  };

  // Handle date selector "go" button
  const handleGoToDate = () => guardNavigation(() => {
    // Create date for first day of selected month/year
    const newDate = moment().year(selectedYear).month(selectedMonthNumber - 1).date(1)
      .toDate();

    // Dispatch the action to update selected date
    dispatch(setSelectedDate(newDate));
    dispatch(setSelectedMonth(selectedMonthNumber));

    // Update local calendar display
    const newCurrentMonth = moment(newDate).startOf('month').toDate();
    const newNextMonth = moment(newDate).add(1, 'month').startOf('month').toDate();
    setCurrentMonth(newCurrentMonth);
    dispatch(setFirstMonthInPicker(newCurrentMonth));
    setNextMonth(newNextMonth);

    // Load data for the new months
    loadDataForMonths(newCurrentMonth, newNextMonth);

    // Hide the date selector
    setShowDateSelector(false);
  });

  // Toggle date selector visibility
  const toggleDateSelector = () => {
    setShowDateSelector(!showDateSelector);
  };

  // Load data for both months on initial render and when dependencies change
  useEffect(() => {
    loadDataForMonths(currentMonth, nextMonth);
  }, [currentUser,
    selectedReduxCalendar,
    selectedReduxAthlete,
  ]);

  useEffect(() => {
    // Get first day of currentMonth and last day of nextMonth (the visible range)
    const firstDayOfCurrentMonth = moment(currentMonth).startOf('month').toDate();
    const lastDayOfNextMonth = moment(nextMonth).endOf('month').toDate();
    // Check if selectedDate is outside the visible range (before or after)
    if (selectedDate < firstDayOfCurrentMonth || selectedDate > lastDayOfNextMonth) {
      // Reset to show the month containing the selectedDate and the following month
      const newCurrentMonth = moment(selectedDate).startOf('month').toDate();
      const newNextMonth = moment(selectedDate).add(1, 'month').startOf('month').toDate();
      setCurrentMonth(newCurrentMonth);
      dispatch(setFirstMonthInPicker(newCurrentMonth));
      setNextMonth(newNextMonth);
      // Load data for both months when calendar view changes due to date selection
      loadDataForMonths(newCurrentMonth, newNextMonth);
    }
  }, [selectedDate]); // Only run when selectedDate changes

  const hasWorkoutItems = (date) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    // Check if this date has assigned items in the picker data
    if (!calendarPickerItems || !Array.isArray(calendarPickerItems)) {
      return false;
    }
    const dateItem = calendarPickerItems.find((item) => item.date === formattedDate);
    return dateItem?.hasAssignedItems || false;
  };

  /**
   * Highlight exactly the occurrences currently displayed in the week-view
   * sliding window (published by WeekView via currentlyDisplayedWeekdays).
   * Scoped to the tile's own month grid so a date that spills into the
   * neighboring calendar isn't double-highlighted.
   */
  const isInDisplayedWeekdays = (date, activeStartDate) => {
    if (!Array.isArray(currentlyDisplayedWeekdays)) return false;
    const formatted = moment(date).format('YYYY-MM-DD');
    const isOutsideCurrentMonth = !moment(date).isSame(activeStartDate, 'month');
    return !isOutsideCurrentMonth
      && currentlyDisplayedWeekdays.some((d) => d.date === formatted);
  };

  const isInSameWeek = (date, refDate) => {
    const start = moment(refDate).startOf('day');
    const end = moment(refDate).add(6, 'days').endOf('day');
    return moment(date).isBetween(start, end, null, '[]'); // inclusive
  };

  const getWeekPosition = (date, refDate) => {
    if (selectedWeekViewDayToggle !== 'Everyday') return undefined;
    if (!isInSameWeek(date, refDate)) return undefined;
    const start = moment(refDate).startOf('day');
    return moment(date).diff(start, 'days');
  };

  const tileContent = (props) => {
    const { date, view, activeStartDate } = props;
    const today = moment();
    const isCurrentDate = moment(date).isSame(today, 'day');
    const dateNumber = date.getDate();
    const isSameMonth = date.getMonth() === activeStartDate.getMonth();
    const isSelected = view === 'month' && selectedDate && (
      viewType === 'weekView'
        ? selectedWeekViewDayToggle !== 'Everyday' ? isInDisplayedWeekdays(date, activeStartDate) : isInSameWeek(date, selectedDate) // Highlight the displayed weekday window, or the entire week
        : moment(date).isSame(selectedDate, 'day') // Highlight single day otherwise
    );
    const hasWorkout = hasWorkoutItems(date);
    const weekPosition = viewType === 'weekView' && selectedDate && isSelected ? getWeekPosition(date, selectedDate) : undefined;

    if (view === 'month') {
      return (
        <DateCircle
          isInMonth={isSameMonth}
          selected={isSelected}
          weekPosition={weekPosition}
          isCurrentDate={isCurrentDate}
        >
          {dateNumber}
          <DotIndicator
            isSelected={isSelected}
            hasWorkout={hasWorkout}
            weekPosition={weekPosition}
            isCurrentDate={isCurrentDate}
          />
        </DateCircle>
      );
    }
    return (
      <DateCircle
        isInMonth={isSameMonth}
        selected={isSelected}
        weekPosition={weekPosition}
        isCurrentDate={isCurrentDate}
      >
        {dateNumber}
      </DateCircle>
    );
  };

  const CustomNextIcon = () => (
    <button
      onClick={() => handleNavigation('next')}
      style={{
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
      }}
      aria-label='Next month'
      type='button'
    >
      <IcomoonReact
        iconSet={iconSet}
        size={15}
        icon='right-arrow'
        color='#444444'
      />
    </button>
  );

  const CustomPrevIcon = () => (
    <button
      onClick={() => handleNavigation('prev')}
      style={{
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
      }}
      aria-label='Previous month'
      type='button'
    >
      <IcomoonReact
        iconSet={iconSet}
        size={15}
        icon='left-arrow'
        color='#444444'
      />
    </button>
  );

  return (
    <StyledCalendarWrapper>
      <DateSelectorDropdown isVisible={showDateSelector}>
        <SelectContainer>
          <Select
            value={monthOptions.find((option) => option.value === selectedMonthNumber)}
            onChange={(option) => setSelectedMonthNumber(option.value)}
            options={monthOptions}
            placeholder='Select Month'
            isSearchable={false}
            components={{
              IndicatorSeparator: () => null,
              DropdownIndicator: () => null,
            }}
            styles={{
              control: (base) => ({
                ...base,
                fontFamily: 'Nunito Sans',
                fontSize: '14px',
                width: '100%',
                maxWidth: '120px',
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              menuList: (base) => ({
                ...base,
                '::-webkit-scrollbar': {
                  width: '8px',
                },
                '::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(136, 136, 136, 0.5)',
                  borderRadius: '10px',
                  transition: 'background-color 0.3s ease',
                },
                '::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0)',
                },
                ':hover::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(136, 136, 136, 1)',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(136, 136, 136, 0.5) transparent',
              }),
            }}
          />
        </SelectContainer>
        <SelectContainer>
          <Select
            value={yearOptions.find((option) => option.value === selectedYear)}
            onChange={(option) => setSelectedYear(option.value)}
            options={yearOptions}
            placeholder='Select Year'
            isSearchable={false}
            components={{
              IndicatorSeparator: () => null,
              DropdownIndicator: () => null,
            }}
            styles={{
              control: (base) => ({
                ...base,
                fontFamily: 'Nunito Sans',
                fontSize: '14px',
                width: '100%',
                maxWidth: '120px',
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              menuList: (base) => ({
                ...base,
                '::-webkit-scrollbar': {
                  width: '8px',
                },
                '::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(136, 136, 136, 0.5)',
                  borderRadius: '10px',
                  transition: 'background-color 0.3s ease',
                },
                '::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0)',
                },
                ':hover::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(136, 136, 136, 1)',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(136, 136, 136, 0.5) transparent',
              }),
            }}
          />
        </SelectContainer>
        <GoButton
          onClick={handleGoToDate}
          disabled={!selectedYear || !selectedMonthNumber}
        >
          GO
        </GoButton>
      </DateSelectorDropdown>

      <StyledCalendar
        activeStartDate={currentMonth}
        formatShortWeekday={(locale, date) => moment(date).format('dddd').substring(0, 1)}
        tileContent={tileContent}
        className='calendar-class'
        onChange={handleDateChange}
        value={selectedDate}
        locale='en-US'
        next2Label={null}
        prev2Label={null}
        nextLabel={<CustomNextIcon />}
        prevLabel={<CustomPrevIcon />}
        navigationLabel={({ label }) => (
          <ClickableNavigationLabel
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleDateSelector();
            }}
          >
            {label}
          </ClickableNavigationLabel>
        )}
        showNeighboringMonth
        onActiveStartDateChange={() => {}}
      />
      <StyledCalendar
        activeStartDate={nextMonth}
        formatShortWeekday={(locale, date) => moment(date).format('dddd').substring(0, 1)}
        tileContent={tileContent}
        className='calendar-class'
        onChange={handleDateChange}
        value={selectedDate}
        locale='en-US'
        next2Label={null}
        prev2Label={null}
        nextLabel={null}
        prevLabel={null}
        navigationLabel={({ label }) => (
          <ClickableNavigationLabel
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleDateSelector();
            }}
          >
            {label}
          </ClickableNavigationLabel>
        )}
        showNeighboringMonth
        onActiveStartDateChange={() => {}}
      />
    </StyledCalendarWrapper>
  );
};

CalendarCalendarPicker.propTypes = { };

export default CalendarCalendarPicker;
